import React, { useState, useRef, useEffect } from "react";

const Hero = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLevel, setSelectedLevel] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [playbackRate, setPlaybackRate] = useState(1);
    const videoRef = useRef(null);

    const handleSearch = (e) => {
        e.preventDefault();
        console.log("Searching for:", { searchTerm, selectedLevel, selectedYear });
    };

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const changePlaybackRate = (rate) => {
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
            setPlaybackRate(rate);
        }
    };

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            const handleLoad = () => {
                setIsVideoLoaded(true);
                video.playbackRate = 0.85;
            };
            
            video.addEventListener('loadeddata', handleLoad);
            video.addEventListener('ended', () => {
                video.currentTime = 0;
                video.play();
            });

            return () => {
                video.removeEventListener('loadeddata', handleLoad);
            };
        }
    }, []);

    return (
        <section id="hero" className="hero-section position-relative overflow-hidden">
            {/* Video Background Container */}
            <div className="video-container position-absolute w-100 h-100">
                {/* Main Video Layer */}
                <div className="video-layer">
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="w-100 h-100 object-fit-cover"
                        poster="/assets/img/video-poster.jpg"
                        style={{ 
                            opacity: isVideoLoaded ? 1 : 0, 
                            transition: 'opacity 1.5s ease',
                            filter: 'brightness(0.7) contrast(1.1) saturate(1.1)'
                        }}
                    >
                        <source src="/assets/course-video1.mp4" type="video/mp4" />
                        <source src="/assets/course-video1.webm" type="video/webm" />
                        <img 
                            src="/assets/img/students-studying.jpg" 
                            alt="Students studying background" 
                            className="w-100 h-100 object-fit-cover"
                        />
                    </video>
                </div>
                
                {/* ENHANCED OVERLAY SYSTEM */}
                <div className="overlay-system position-absolute w-100 h-100">
                    
                    {/* Primary Dark Overlay - The Key Improvement */}
                    <div className="primary-overlay position-absolute w-100 h-100"
                         style={{
                             background: 'linear-gradient(135deg, rgba(58, 63, 68, 0.85) 0%, rgba(231, 231, 231, 0) 50%, rgba(8, 8, 8, 0.85) 100%)',
                             mixBlendMode: 'multiply'
                         }}>
                    </div>
                    
                    {/* Secondary Gradient Overlay for Depth */}
                    <div className="gradient-overlay position-absolute w-100 h-100"
                         style={{
                             background: 'linear-gradient(45deg, rgba(59, 130, 246, 0.25) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(185, 192, 190, 0.1) 100%)',
                             opacity: 0.4,
                             mixBlendMode: 'soft-light'
                         }}>
                    </div>
                    
                    {/* Subtle Grid Pattern for Texture */}
                    <div className="grid-overlay position-absolute w-100 h-100"
                         style={{
                             backgroundImage: `
                                 linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                                 linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
                             `,
                             backgroundSize: '50px 50px',
                             opacity: 0.3
                         }}>
                    </div>
                    
                    {/* Vignette Effect */}
                    <div className="vignette position-absolute w-100 h-100"
                         style={{
                             background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0, 0, 0, 0.7) 100%)',
                             opacity: 0.6
                         }}>
                    </div>
                    
                    {/* Brightness Control Layer */}
                    <div className="brightness-control position-absolute w-100 h-100"
                         style={{
                             background: 'rgba(7, 7, 7, 0.23)',
                             mixBlendMode: 'darken'
                         }}>
                    </div>
                    
                    {/* Subtle Grain/Noise Texture */}
                    <div className="noise-texture position-absolute w-100 h-100"
                         style={{
                             backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='1' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                             opacity: 0.03,
                             mixBlendMode: 'overlay'
                         }}>
                    </div>
                </div>

                {/* Video Controls */}
                <div className="video-controls position-absolute bottom-4 end-4 d-flex gap-2" style={{ zIndex: 20 }}>
                    <button 
                        onClick={togglePlayPause}
                        className="btn btn-sm btn-dark bg-opacity-75 border-0 rounded-circle p-2"
                        aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                        <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                    </button>
                    <div className="btn-group">
                        <button 
                            onClick={() => changePlaybackRate(0.5)}
                            className={`btn btn-sm btn-dark bg-opacity-75 border-0 ${playbackRate === 0.5 ? 'active' : ''}`}
                        >
                            0.5x
                        </button>
                        <button 
                            onClick={() => changePlaybackRate(1)}
                            className={`btn btn-sm btn-dark bg-opacity-75 border-0 ${playbackRate === 1 ? 'active' : ''}`}
                        >
                            1x
                        </button>
                        <button 
                            onClick={() => changePlaybackRate(1.5)}
                            className={`btn btn-sm btn-dark bg-opacity-75 border-0 ${playbackRate === 1.5 ? 'active' : ''}`}
                        >
                            1.5x
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Container */}
            <div className="content-container position-relative w-100 py-5 mt-2 min-vh-100 d-flex align-items-center">
                <div className="container position-relative" style={{ zIndex: 15 }}>
                    <div className="row justify-content-center">
                        <div className="col-lg-10 text-center">
                            <div className="eyebrow-wrapper mb-4" data-aos="fade-down" data-aos-delay="50">
                                <div className="eyebrow d-inline-flex align-items-center px-3 py-1 rounded-pill bg-dark bg-opacity-50 border border-light border-opacity-25">
                                    <i className="bi bi-stars me-2 text-warning"></i>
                                    <span className="text-light fw-medium">Your Ultimate Exam Success Partner</span>
                                    <div className="ms-3 px-2 py-1 rounded bg-warning bg-opacity-10 border border-warning border-opacity-25">
                                        <small className="text-warning">NEW</small>
                                    </div>
                                </div>
                            </div>

                            {/* Main Heading */}
                            <h1 className="display-5 fw-bold mb-4 text-white" 
                                data-aos="fade-up" 
                                data-aos-delay="100"
                                style={{
                                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
                                }}>
                                <span>Master Exams With</span>
                                <br />
                                <span className="text-warning">Verified Past Questions</span>
                            </h1>

                            {/* Subheading */}
                            <p className="lead mb-5 text-white opacity-95 max-w-800 mx-auto fs-4" 
                               data-aos="fade-up" 
                               data-aos-delay="150">
                                Access thousands of past questions from BECE, WASSCE, CTVET to University levels. 
                                Practice with real exam papers and study with expert-verified answers.
                            </p>

                            {/* Search Form */}
                            {/* <div className="search-section" data-aos="fade-up" data-aos-delay="200">
                                <div className="search-card bg-white bg-opacity-95 p-4 p-md-5 rounded-4 shadow-lg border-0">
                                    <div className="search-header mb-4">
                                        <h3 className="h5 text-dark mb-2">
                                            <i className="bi bi-search me-2 text-primary"></i>
                                            Find Your Study Materials
                                        </h3>
                                        <p className="text-dark opacity-75 mb-0">
                                            Search across 10,000+ verified past questions
                                        </p>
                                    </div>
                                    
                                    <form onSubmit={handleSearch} className="row g-3">
                                        <div className="col-lg-4">
                                            <div className="input-group input-group-lg shadow-sm">
                                                <span className="input-group-text bg-white border-end-0 border-secondary">
                                                    <i className="bi bi-search text-dark"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control bg-white border-start-0 border-secondary text-dark"
                                                    placeholder="Search by subject, topic, or keyword..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="col-lg-3">
                                            <div className="position-relative">
                                                <select 
                                                    className="form-select form-select-lg bg-white border-secondary text-dark shadow-sm"
                                                    value={selectedLevel}
                                                    onChange={(e) => setSelectedLevel(e.target.value)}
                                                >
                                                    <option value="" className="text-dark">All Education Levels</option>
                                                    <option value="bece" className="text-dark">BECE (JHS)</option>
                                                    <option value="shs" className="text-dark">SHS/WASSCE</option>
                                                    <option value="CTVET" className="text-dark">CTVET Exams</option>
                                                    <option value="university" className="text-dark">University</option>
                                                </select>
                                                <i className="bi bi-chevron-down position-absolute end-3 top-50 translate-middle-y text-dark opacity-50"></i>
                                            </div>
                                        </div>
                                        
                                        <div className="col-lg-3">
                                            <div className="position-relative">
                                                <select 
                                                    className="form-select form-select-lg bg-white border-secondary text-dark shadow-sm"
                                                    value={selectedYear}
                                                    onChange={(e) => setSelectedYear(e.target.value)}
                                                >
                                                    <option value="" className="text-dark">Any Year (2000-2024)</option>
                                                    {Array.from({length: 25}, (_, i) => {
                                                        const year = new Date().getFullYear() - i;
                                                        return <option key={year} value={year} className="text-dark">{year}</option>;
                                                    })}
                                                </select>
                                               
                                            </div>
                                        </div>
                                        
                                        <div className="col-lg-2">
                                            <button type="submit" className="btn btn-primary btn-lg w-100 shadow-sm">
                                                <i className="bi bi-search me-2"></i>
                                                Search
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div> */}

                            {/* Quick Stats */}
                            <div className="stats-section mt-5" data-aos="fade-up" data-aos-delay="300">
                                <div className="stats-grid d-flex flex-wrap justify-content-center gap-4">
                                    {[
                                        { value: "20+", label: "Past Questions", icon: "bi-journal-text", color: "primary" },
                                        { value: "300+", label: "Happy Students", icon: "bi-people", color: "success" },
                                        { value: "5+", label: "Years Coverage", icon: "bi-calendar-range", color: "warning" },
                                        { value: "99%", label: "Accuracy Rate", icon: "bi-check-circle", color: "info" }
                                    ].map((stat, index) => (
                                        <div key={index} className="stat-card text-center p-4 rounded-3 shadow-sm bg-white bg-opacity-95 border-0"
                                             style={{
                                                 minWidth: '120px',
                                                 transition: 'all 0.3s ease'
                                             }}>
                                            <div className={`display-5 fw-bold text-${stat.color} mb-2`}>
                                                {stat.value}
                                            </div>
                                            <div className="text-dark opacity-90">
                                                <i className={`bi ${stat.icon} me-2 text-${stat.color}`}></i>
                                                {stat.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick Links */}
                            <div className="quick-links mt-5" data-aos="fade-up" data-aos-delay="400">
                                <p className="text-white mb-4">Popular subjects:</p>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    {[
                                        { label: "BECE Mathematics", href: "/questions", icon: "bi-calculator" },
                                        { label: "WASSCE Biology", href: "/questions", icon: "bi-flower1" },
                                        { label: "WASSCE English", href: "/questions", icon: "bi-translate" },
                                        { label: "University Accounting", href: "/questions", icon: "bi-calculator-fill" },
                                        { label: "CTVET Science", href: "/questions", icon: "bi-bezier" }
                                    ].map((link, index) => (
                                        <a key={index} 
                                           href={link.href}
                                           className="btn btn-light btn-hover-lift rounded-pill px-4 py-2 shadow-sm"
                                           style={{
                                               transition: 'all 0.3s ease'
                                           }}>
                                            <i className={`${link.icon} me-2 text-primary`}></i>
                                            {link.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </section>
    );
};

export default Hero;